export type CharacterClass = 'tank' | 'archer' | 'mage' | 'rogue';
export type Gender = 'male' | 'female';

export interface Character {
  id: string;
  name: string;
  characterClass: CharacterClass;
  gender: Gender;
  level: number;
  experience: number;
  isActive: boolean;
  slotNumber: number;
  appearanceData?: string;
  createdAt: Date;
  lastPlayedAt: Date;
  totalPlayTimeMinutes: number;
  equipment?: CharacterEquipment[];
}

export interface CharacterEquipment {
  slot: ItemCategory;
  itemId: string;
  itemCode?: string;
  itemName?: string;
  styling?: string;
}

export type ItemCategory =
  | 'head'
  | 'face'
  | 'torso'
  | 'hands'
  | 'legs'
  | 'feet'
  | 'back'
  | 'shoulder'
  | 'neck'
  | 'waist'
  | 'wings'
  | 'aura'
  | 'trail'
  | 'mainhand'
  | 'offhand'
  | 'twohand'
  | 'pet';

export type ItemRarity =
  | 'common'
  | 'uncommon'
  | 'rare'
  | 'epic'
  | 'legendary'
  | 'mythic';

export interface WardrobeItem {
  id: string;
  itemCode: string;
  displayName: string;
  description?: string;
  category: ItemCategory;
  rarity: ItemRarity;
  assetBundleName?: string;
  assetPath?: string;
  thumbnailUrl?: string;
  premiumPrice: number;
  coinPrice: number;
  isAvailable: boolean;
  isDefault: boolean;
}

export interface UserWardrobeItem {
  id: string;
  wardrobeItemId: string;
  acquiredAt: Date;
  acquisitionSource?: string;
  isFavorite: boolean;
  customization?: string;
  item?: WardrobeItem;
}

export interface EquippedItem {
  id: string;
  slot: ItemCategory;
  wardrobeItemId: string;
  styling?: string;
  equippedAt: Date;
  item?: WardrobeItem;
}

export const CHARACTER_CLASSES: { value: CharacterClass; label: string; description: string; icon: string }[] = [
  { value: 'tank', label: 'Tank', description: 'Strong and resilient warrior', icon: '\uD83D\uDEE1\uFE0F' },
  { value: 'archer', label: 'Archer', description: 'Swift and precise ranged fighter', icon: '\uD83C\uDFF9' },
  { value: 'mage', label: 'Mage', description: 'Powerful magic wielder', icon: '\uD83E\uDDD9' },
  { value: 'rogue', label: 'Rogue', description: 'Stealthy and cunning assassin', icon: '\uD83D\uDDE1\uFE0F' },
];

export const ITEM_CATEGORIES: { value: ItemCategory; label: string; icon: string }[] = [
  { value: 'head', label: 'Head', icon: '\uD83E\uDDE2' },
  { value: 'face', label: 'Face', icon: '\uD83D\uDC53' },
  { value: 'torso', label: 'Torso', icon: '\uD83D\uDC55' },
  { value: 'hands', label: 'Hands', icon: '\uD83E\uDDE4' },
  { value: 'legs', label: 'Legs', icon: '\uD83D\uDC56' },
  { value: 'feet', label: 'Feet', icon: '\uD83D\uDC5F' },
  { value: 'back', label: 'Back', icon: '\uD83C\uDF92' },
  { value: 'shoulder', label: 'Shoulder', icon: '\uD83E\uDDB9' },
  { value: 'neck', label: 'Neck', icon: '\uD83D\uDCFF' },
  { value: 'waist', label: 'Waist', icon: '\uD83E\uDE96' },
  { value: 'wings', label: 'Wings', icon: '\uD83E\uDEB6' },
  { value: 'aura', label: 'Aura', icon: '\u2728' },
  { value: 'trail', label: 'Trail', icon: '\uD83C\uDF20' },
  { value: 'mainhand', label: 'Main Hand', icon: '\u2694\uFE0F' },
  { value: 'offhand', label: 'Off Hand', icon: '\uD83D\uDEE1\uFE0F' },
  { value: 'twohand', label: 'Two Hand', icon: '\uD83D\uDDE1\uFE0F' },
  { value: 'pet', label: 'Pet', icon: '\uD83D\uDC3E' },
];

export const RARITY_COLORS: Record<ItemRarity, string> = {
  common: '#9e9e9e',
  uncommon: '#4caf50',
  rare: '#2196f3',
  epic: '#9c27b0',
  legendary: '#ff9800',
  mythic: '#e91e63',
};

export const RARITY_ORDER: ItemRarity[] = ['common', 'uncommon', 'rare', 'epic', 'legendary', 'mythic'];
